import type { PiniaPluginContext } from "pinia";
import type { AppUserStoreUser } from "@/stores/app-user";

const LS_APP_KEY = "partnerPortal";

// Detects the pre-normalization permission shape, where each permission array held
// LocationSummary objects instead of id strings. Users with a stale session from
// before the deploy would crash dashboards until their next login/refresh, so we
// force a sign-out on hydration if we detect the old shape.
const isOldPermissionShape = (user: AppUserStoreUser | undefined): boolean => {
  if (!user?.permissions) return false;
  // Scan every entry; old-shape event arrays prefix a venue:<id> string before
  // LocationSummary objects, so stopping at the first non-null entry misses them.
  for (const key of Object.keys(user.permissions)) {
    const arr = user.permissions[key] as unknown[];
    if (!Array.isArray(arr)) continue;
    for (const entry of arr) {
      if (entry != null && typeof entry !== "string") return true;
    }
  }
  return false;
};

export default function PersistAppUserStorePlugin(context: PiniaPluginContext) {
  if (context.store.$id !== "appUser") {
    return {};
  }

  const user = JSON.parse(
    localStorage.getItem(`${LS_APP_KEY}:user`) || "{}",
  ) as AppUserStoreUser;

  if (user?.attributes) {
    if (isOldPermissionShape(user)) {
      // Clear stale session; user will re-login and receive the new shape.
      localStorage.removeItem(`${LS_APP_KEY}:user`);
      localStorage.removeItem(`${LS_APP_KEY}:accessToken`);
      localStorage.removeItem(`${LS_APP_KEY}:refreshToken`);
    } else {
      if (!user.locations) {
        user.locations = {};
      }
      // Backfill groups for sessions persisted before the groups field existed,
      // so the non-optional user.groups type holds after hydration.
      if (!user.groups) {
        user.groups = [];
      }
      context.store.user = user;

      context.store.accessToken =
        localStorage.getItem(`${LS_APP_KEY}:accessToken`) ?? "";
      context.store.refreshToken =
        localStorage.getItem(`${LS_APP_KEY}:refreshToken`) ?? "";
    }
  }

  const persistUser = () => {
    const currentUser = context.store.user;
    if (currentUser) {
      localStorage.setItem(`${LS_APP_KEY}:user`, JSON.stringify(currentUser));
    }
  };

  context.store.$onAction((event) => {
    if (event.name === "setUserAndTokens") {
      const [user, accessToken, refreshToken] = event.args as [
        AppUserStoreUser,
        string,
        string,
      ];

      localStorage.setItem(`${LS_APP_KEY}:user`, JSON.stringify(user));
      localStorage.setItem(`${LS_APP_KEY}:accessToken`, accessToken);
      localStorage.setItem(`${LS_APP_KEY}:refreshToken`, refreshToken);
    }

    if (event.name === "setPermissionsAndLocations") {
      // Run after the mutation completes so the persisted snapshot reflects both fields.
      event.after(() => persistUser());
    }

    if (event.name === "signOut") {
      localStorage.removeItem(`${LS_APP_KEY}:user`);
      localStorage.removeItem(`${LS_APP_KEY}:accessToken`);
      localStorage.removeItem(`${LS_APP_KEY}:refreshToken`);
    }
  });

  return {};
}
